import React from 'react';
import { Globe, Server, Radio, Shield, AlertTriangle, AlertOctagon, AlertCircle, ExternalLink } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Badge } from '../ui/Badge';
import { TechnologyStack } from '../assets/TechnologyStack';

interface Technology {
  name: string;
  logo_path: string;
}

interface WebApp {
  id: string;
  name: string;
  url: string;
  screenshot: string;
  technologies: Technology[];
  ports: string[];
  domain: string;
  ip: string;
  status: string;
  lastScan: string;
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface WebAppCardProps {
  app: any;
}

const linkedObject: any = {
  "Subdomain": "subdomains",
  "Domain": "domains",
  "IP Address": "ip-addresses"
}

export const WebAppCard: React.FC<WebAppCardProps> = ({ app }) => {
  const getVulnerabilityIcon = (count: number, type: 'critical' | 'high' | 'medium' | 'low') => {
    if (count === 0) return null;

    const icons = {
      critical: { icon: AlertOctagon, color: 'text-red-500' },
      high: { icon: AlertTriangle, color: 'text-orange-500' },
      medium: { icon: AlertCircle, color: 'text-yellow-500' },
      low: { icon: Shield, color: 'text-emerald-500' }
    };

    const { icon: Icon, color } = icons[type];
    
    return (
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className={`flex items-center space-x-1 ${color}`}>
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{count}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
              sideOffset={5}
            >
              {count} {type} vulnerabilities
              <Tooltip.Arrow className="fill-[#1A1F2E]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  };

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 group">
      {/* Screenshot */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={app.screenshot}
          alt={app.linkedObject.value}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1629] to-transparent opacity-50" />

      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-white">{app.title}</h3>
            <a
              href={`${app.service}://${app.linkedObject.value}:${app.number}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-1 cursor-pointer" onClick={() => window.open(`/${linkedObject[app.linkedObject.type]}/${app.linkedObject.id}`, '_blank')}>
              <Globe className="h-4 w-4" />
              <span>{app.linkedObject.value}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Server className="h-4 w-4" />
              <span>{app.protocol.toUpperCase()}</span>
            </div>
          </div>
        </div>

        {/* Technologies */}
        <div className="flex flex-wrap gap-2">
          <TechnologyStack technologies={app.technologies} maxVisible={5} />
        </div>

        {/* Ports */}
        <div className="flex items-center space-x-2">
          <Radio className="h-4 w-4 text-gray-500" />
          <div className="flex items-center gap-2">
            
              <span
                key={app.number}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400 cursor-pointer"
                onClick={() => window.open(`/ports/${app.number}`, '_blank')}
              >
                Port {app.number}
              </span>
              <span
                key={app.number}
                className="px-2 py-0.5 text-xs rounded-full bg-blue-500/50 text-gray-300"
              >
                {app.state}
              </span>
              <span
                key={app.number}
                className="px-2 py-0.5 text-xs rounded-full bg-blue-500/50 text-gray-300"
              >
                {app.statusCode}
              </span>
          </div>
        </div>


      </div>
    </div>
  );
};